/**
 * Twin commonality resource (M8.4) — the "why did this fail?" group comparison.
 *
 * POST /v1/twin/commonality compares a FAILURE population (scoped by
 * defect_type | part_id | heat_id) against a REFERENCE population and returns,
 * per dimension (equipment | lot | parameter), the over-represented upstream
 * features ranked by lift, each with a chi-square p-value + support.
 *
 * Wire shapes mirror platform-core/internal/twin/commonality.go exactly.
 */

import { api } from "@/lib/api-client";

export type CommonalityDimension = "equipment" | "lot" | "parameter";

export interface CommonalityScope {
  defect_type?: string;
  part_id?: string;
  heat_id?: string;
}

export interface CommonalityRequest {
  scope: CommonalityScope;
  window_days?: number;
  dimensions?: CommonalityDimension[];
  min_support?: number;
  top_n?: number;
}

export interface CommonalityRow {
  dimension: CommonalityDimension;
  feature: string;
  label: string;
  fail_with: number;
  fail_total: number;
  ref_with: number;
  ref_total: number;
  fail_rate: number;
  base_rate: number;
  lift: number;
  p_value: number;
  support: number;
  /** # of statistically-identical features folded into this row (≥1). */
  collapsed?: number;
}

export interface CommonalityCell {
  paramA: string;
  paramB: string;
  r: number;
  pValue: number;
  n: number;
}

export interface CommonalityResult {
  scope: CommonalityScope;
  fail_total: number;
  ref_total: number;
  dimensions: CommonalityDimension[];
  rows: CommonalityRow[];
  cells: CommonalityCell[];
}

export const twinCommonality = {
  /** Run a defect-commonality comparison for a scoped failure population. */
  analyze: (body: CommonalityRequest) =>
    api.post<CommonalityResult>(`/v1/twin/commonality`, body),
};
